import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, AlertCircle, CheckCircle, Loader2, X, Globe, Building2, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { initFacebookSDK, loginWithFacebook, generateVerifyToken } from '../lib/facebook';

interface BusinessProfile {
  name: string;
  description: string;
  category: string;
  timezone: string;
}

const TIMEZONES = [
  { value: 'America/Argentina/Buenos_Aires', label: '(GMT-03:00) Buenos Aires' },
  { value: 'America/Santiago', label: '(GMT-04:00) Santiago' },
  { value: 'America/Bogota', label: '(GMT-05:00) Bogotá' },
  { value: 'America/Mexico_City', label: '(GMT-06:00) Ciudad de México' },
  { value: 'America/Lima', label: '(GMT-05:00) Lima' },
];

const CATEGORIES = [
  'Comercio minorista',
  'Servicios profesionales',
  'Restaurante',
  'Educación',
  'Salud y bienestar',
  'Tecnología',
  'Otros',
];

export default function ConnectWhatsApp() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [step, setStep] = useState<'profile' | 'facebook'>('profile');
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile>({
    name: '',
    description: '',
    category: '',
    timezone: 'America/Argentina/Buenos_Aires',
  });

  useEffect(() => {
    initFacebookSDK()
      .then(() => setSdkReady(true))
      .catch(err => setError(err.message));
  }, []);

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!businessProfile.name.trim()) {
      setError('El nombre del negocio es requerido');
      return;
    }

    if (!businessProfile.category) {
      setError('La categoría es requerida');
      return;
    }

    setStep('facebook');
  };

  const handleFacebookConnect = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      if (!user) {
        throw new Error('Usuario no autenticado');
      }

      // Get Facebook access token and phone number ID
      const { accessToken, phoneNumberId } = await loginWithFacebook();
      
      // Generate verify token
      const verifyToken = generateVerifyToken();

      // Create instance with business profile
      const { error: insertError } = await supabase
        .from('instances')
        .insert({
          user_id: user.id,
          name: businessProfile.name,
          phone_number_id: phoneNumberId,
          access_token: accessToken,
          verify_token: verifyToken,
          is_active: true,
          business_profile: {
            ...businessProfile,
            setup_date: new Date().toISOString()
          }
        });

      if (insertError) {
        if (insertError.code === '23505') {
          throw new Error('Ya existe un canal con este número de teléfono');
        }
        throw insertError;
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (err: any) {
      console.error('Error connecting WhatsApp:', err);
      setError(err.message || 'Error al conectar con WhatsApp Business');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center">
          <MessageSquare className="mx-auto h-12 w-12 text-[#25D366]" />
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Conectar WhatsApp Business
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Configura tu perfil de empresa y conecta tu cuenta de WhatsApp Business en minutos
          </p>
        </div>

        <div className="mt-8">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {success ? (
            <div className="text-center bg-white p-8 rounded-lg shadow">
              <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Conexión exitosa</h3>
              <p className="mt-1 text-sm text-gray-500">
                Tu cuenta de WhatsApp fue conectada correctamente. Ya podés comenzar a usar el bot o enviar campañas.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              {step === 'profile' ? (
                <form onSubmit={handleProfileSubmit}>
                  <div className="p-6 space-y-6">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <Building2 className="h-5 w-5 mr-2 text-[#25D366]" />
                      Perfil de empresa
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Nombre de la empresa *
                        </label>
                        <input
                          type="text"
                          value={businessProfile.name}
                          onChange={(e) => setBusinessProfile({
                            ...businessProfile,
                            name: e.target.value
                          })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#25D366] focus:ring-[#25D366]"
                          placeholder="Ej: Mi Empresa S.A."
                          required
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Este nombre será visible para tus clientes en WhatsApp
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Categoría *
                        </label>
                        <select
                          value={businessProfile.category}
                          onChange={(e) => setBusinessProfile({
                            ...businessProfile,
                            category: e.target.value
                          })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#25D366] focus:ring-[#25D366]"
                          required
                        >
                          <option value="">Selecciona una categoría</option>
                          {CATEGORIES.map(category => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Descripción
                        </label>
                        <textarea
                          value={businessProfile.description}
                          onChange={(e) => setBusinessProfile({
                            ...businessProfile,
                            description: e.target.value
                          })}
                          rows={3}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#25D366] focus:ring-[#25D366]"
                          placeholder="Cuéntales a tus clientes sobre tu empresa..."
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Máximo 512 caracteres
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Zona horaria *
                        </label>
                        <select
                          value={businessProfile.timezone}
                          onChange={(e) => setBusinessProfile({
                            ...businessProfile,
                            timezone: e.target.value
                          })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#25D366] focus:ring-[#25D366]"
                          required
                        >
                          {TIMEZONES.map(tz => (
                            <option key={tz.value} value={tz.value}>
                              {tz.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => navigate('/')}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#25D366] hover:bg-[#128C7E]"
                    >
                      Continuar
                    </button>
                  </div>
                </form>
              ) : (
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
                    <Globe className="h-5 w-5 mr-2 text-[#1877F2]" />
                    Conectar con Facebook
                  </h3>

                  <p className="text-sm text-gray-600 mb-6">
                    Para completar la configuración, necesitamos conectar tu cuenta de Facebook
                    y obtener acceso a WhatsApp Business.
                  </p>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setStep('profile')}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Atrás
                    </button>
                    <button
                      onClick={handleFacebookConnect}
                      disabled={!sdkReady || loading}
                      className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#1877F2] hover:bg-[#166FE5] disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Conectando...
                        </>
                      ) : (
                        'Conectar con Facebook'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}